import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog as MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FetchMaterialTestLabListRequest, DeleteExternalLabLookupRequest } from '../../../../../../../core/projects.actions';
import { externalLabListInfo } from '../../../../../../../core/projects.selectors';
import { ConfirmDialogComponent, ConfirmDialogModel } from '../../../../../../../../../shared/components';
import { CreateOrEditExternalLabComponent } from '../create-or-edit-external-lab/create-or-edit-external-lab.component';
import { ProjectsService } from '../../../../../../../core/projects.service';

@Component({
    selector: 'app-external-lab-setup-screen',
    templateUrl: './external-lab-setup-screen.component.html',
    styleUrls: ['./external-lab-setup-screen.component.scss'],
    standalone: false
})
export class ExternalLabSetupScreenComponent implements OnInit, OnDestroy {
  projectId: string | null = null;
  selectedRow: any = null;

  public dataSource: any[] = [];
  public filteredDataSource: any[] = [];
  public originalDataSource: any[] = [];
  public displayedColumns: string[] = ['name', 'contacts', 'phone', 'email'];
  public totalRecords = 0;
  public pagination = {
    pageIndex: 0,
    pageSize: 10,
  };
  public currentPage = 1;
  public itemsPerPage = 10;
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    private projectsService: ProjectsService,
    private dialog: MatDialog,
    private store: Store,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    if (this.route.snapshot.paramMap.has('projectId')) {
      this.projectId = this.route.snapshot.paramMap.get('projectId');
    }

    this.route.queryParams
      .pipe(takeUntil(this.onDestroy))
      .subscribe(qp => {
        if (qp.subTab === 'external-labs') {
          this.fetchRecords();
        }
      });

    this.store
      .select(externalLabListInfo)
      .pipe(takeUntil(this.onDestroy))
      .subscribe(data => {
        if (data) {
          data = JSON.parse(JSON.stringify(data));
          this.currentPage = 1;

          if (data && data?.list?.length > 0) {
            this.originalDataSource = data?.list;
            this.filteredDataSource = [...this.originalDataSource];
            this.totalRecords = data.totalRecords || this.originalDataSource.length;

            this.paginateData(true);
          } else {
            this.originalDataSource = [];
            this.filteredDataSource = [];
            this.dataSource = [];
            this.totalRecords = 0;
          }
        }
      });
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  fetchRecords(): void {
    if (this.projectId) {
      this.store.dispatch(
        FetchMaterialTestLabListRequest({
          payload: { projectId: this.projectId },
        })
      );
    }
  }

  onPageChanged(page: number): void {
    this.currentPage = page;
    this.pagination.pageIndex = page - 1;
    this.paginateData();
  }

  paginateData(reset = false): void {
    if (reset) {
      this.currentPage = 1;
    }
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = Math.min(startIndex + this.itemsPerPage, this.filteredDataSource.length);
    this.dataSource = this.filteredDataSource.slice(startIndex, endIndex);
  }

  rowClicked(row: any): void {
    this.selectedRow = row;
    this.projectsService.openRightPanel.next({
      component: CreateOrEditExternalLabComponent,
      data: {
        ...row,
        projectId: this.projectId,
      },
    });
  }

  create(): void {
    this.projectsService.openRightPanel.next({
      component: CreateOrEditExternalLabComponent,
      data: {
        projectId: this.projectId,
      },
    });
  }

  deleteItem(item: any, event?: Event): void {
    event?.stopPropagation();
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: true,
      data: new ConfirmDialogModel(
        'Delete',
        `Are you sure you want to delete "${item?.name || item?.value}"?`,
      ),
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && this.projectId) {
        const payload = {
          projectId: this.projectId,
          type: 'external_lab',
          id: item?.id,
        };
        this.store.dispatch(
          DeleteExternalLabLookupRequest({
            payload,
          })
        );
      }
    });
  }
}
